import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Wallet, Loader2, RefreshCw, CalendarDays, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PayrollOverview = () => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/payroll-overview`, getAuthHeader());
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const payroll = data?.upcoming_payroll;

  return (
    <Card className="p-6" data-testid="payroll-overview-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet size={20} className="text-emerald-600" />
          <h2 className="text-lg font-semibold text-gray-900">Payroll Overview</h2>
        </div>
        <button type="button" onClick={() => { setLoading(true); fetchData(); }} data-testid="payroll-overview-refresh-btn"
          className="text-gray-400 hover:text-gray-600" title="Refresh">
          <RefreshCw size={15} />
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-6 justify-center">
          <Loader2 className="animate-spin" size={16} /> Loading payroll…
        </div>
      ) : !data ? (
        <div className="text-center py-6 text-gray-400 text-sm">Failed to load payroll data.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Needed Cash */}
          <div className="border rounded-lg p-5 bg-white" data-testid="payroll-needed-cash">
            <p className="text-sm font-medium text-gray-500 mb-1">Needed Cash</p>
            <p className="text-xs text-gray-400 mb-3">All open + in progress jobs</p>
            <p className="text-3xl font-bold text-gray-900" data-testid="payroll-needed-cash-total">
              {fmt(data.needed_cash.total)}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {data.needed_cash.job_count} job{data.needed_cash.job_count === 1 ? '' : 's'}
            </p>
          </div>
          {/* Upcoming Payroll */}
          <div className="border rounded-lg p-5 bg-white" data-testid="payroll-upcoming">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Upcoming Payroll</p>
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <CalendarDays size={12} />
                  Payday {payroll.payday_label} · maps completed {payroll.window_start} → {payroll.window_end}
                </p>
              </div>
              <p className="text-2xl font-bold text-emerald-700 whitespace-nowrap" data-testid="payroll-upcoming-total">
                {fmt(payroll.total)}
              </p>
            </div>
            {payroll.workers.length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-sm flex flex-col items-center gap-1.5">
                <Users size={18} />
                No cleared worker pay for this payday yet.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {payroll.workers.map((w) => (
                  <div key={w.user_id} className="flex items-center justify-between text-sm border-b border-gray-100 last:border-0 pb-1.5 last:pb-0"
                    data-testid={`payroll-worker-row-${w.user_id}`}>
                    <span className="text-gray-800 truncate">
                      {w.name}
                      <span className="text-xs text-gray-400 ml-1.5">
                        {w.session_count} map{w.session_count === 1 ? '' : 's'}
                      </span>
                    </span>
                    <span className="font-semibold text-gray-900 whitespace-nowrap" data-testid={`payroll-worker-total-${w.user_id}`}>
                      {fmt(w.total)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default PayrollOverview;
